import { isLetterHtml, plainToLetterHtml } from "@/lib/letterHtml";

/**
 * Replace preserved signatory tokens in an issued letter body with snapshot images/text.
 */
export function resolveSignatoryPlaceholders(body, letter) {
  if (!body) return body;
  let out = body;
  const name = (letter?.signatory_name || "").trim();
  // signatory_title aliases designation; whitespace-only counts as absent
  const designation = (
    letter?.signatory_designation || letter?.signatory_title || ""
  ).trim();
  const reqSig = letter?.requires_signature !== false;
  const reqStamp = !!letter?.requires_stamp;
  const companyName = (letter?.company_name || "").trim();
  const sigSource = letter?.signatory_source || "document_approvals";

  out = out.replace(/\{\{\s*signatory_name\s*\}\}/gi, name);
  out = out.replace(/\{\{\s*signatory_designation\s*\}\}/gi, designation);
  out = out.replace(/\{\{\s*signatory_title\s*\}\}/gi, designation);

  if (letter?.signature_data_url && reqSig) {
    out = out.replace(
      /\{\{\s*signature\s*\}\}/gi,
      `<img src="${letter.signature_data_url}" alt="Signature" style="max-height:64px;background:transparent" />`,
    );
  } else {
    out = out.replace(/\{\{\s*signature\s*\}\}/gi, "");
  }

  if (letter?.stamp_data_url && reqStamp) {
    out = out.replace(
      /\{\{\s*stamp\s*\}\}/gi,
      `<img src="${letter.stamp_data_url}" alt="Company stamp" style="max-height:80px;background:transparent" />`,
    );
  } else {
    out = out.replace(/\{\{\s*stamp\s*\}\}/gi, "");
  }

  if (out.match(/\{\{\s*authorized_signatory\s*\}\}/i)) {
    let replacement = "";
    if (sigSource === "template_fallback") {
      // Factory closing: Human Resources + bold company — no empty signature block
      replacement = `Human Resources${companyName ? `<br/><b>${companyName}</b>` : ""}`;
    } else {
      // Document Approvals snapshot (name / designation / signature / company)
      const forLine = companyName ? `For <b>${companyName}</b>` : "For the Company";
      const bits = ['<div class="letter-signatory-block" style="margin-top:1.5em">'];
      if (reqSig) {
        bits.push(`<div>${forLine}</div>`);
        if (letter?.signature_data_url) {
          bits.push(
            `<div style="margin:6px 0"><img src="${letter.signature_data_url}" alt="Signature" style="max-height:64px;background:transparent" /></div>`,
          );
        }
        if (name) bits.push(`<div>${name}</div>`);
        if (designation) bits.push(`<div>${designation}</div>`);
      }
      if (reqStamp && letter?.stamp_data_url) {
        bits.push(
          `<div style="margin-top:8px"><img src="${letter.stamp_data_url}" alt="Company stamp" style="max-height:80px;background:transparent" /></div>`,
        );
      }
      bits.push("</div>");
      replacement = bits.join("");
    }
    // Replace whole lone paragraph to avoid empty <p></p> remnants after the block
    out = out.replace(
      /<p>\s*\{\{\s*authorized_signatory\s*\}\}\s*<\/p>/gi,
      sigSource === "template_fallback" ? `<p>${replacement}</p>` : replacement,
    );
    out = out.replace(/\{\{\s*authorized_signatory\s*\}\}/gi, replacement);
  }

  return out;
}

/** Read-only letter body — renders HTML when present, else plain text. */
export default function LetterBodyView({
  body,
  letter = null,
  className = "",
  testid = "letter-body-view",
  baseStyle = null,
}) {
  const resolved = letter ? resolveSignatoryPlaceholders(body, letter) : body;
  if (!resolved) {
    return <div className={className} data-testid={testid} />;
  }
  if (isLetterHtml(resolved)) {
    return (
      <>
        <div
          data-testid={testid}
          className={`letter-body-html text-sm text-slate-800 leading-7 ${className}`}
          style={baseStyle || undefined}
          dangerouslySetInnerHTML={{ __html: resolved }}
        />
        <style>{`
          .letter-body-html p { margin: 0 0 0.75em; }
          .letter-body-html h2 { font-size: 1.15rem; font-weight: 700; margin: 0 0 0.6em; }
          .letter-body-html ul, .letter-body-html ol { margin: 0 0 0.75em; padding-left: 1.4rem; }
          .letter-body-html li { margin: 0.15em 0; }
          .letter-body-html table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
          .letter-body-html th, .letter-body-html td { border: 1px solid #94a3b8; padding: 6px; }
          .letter-body-html th { background: #f1f5f9; font-weight: 600; }
          .letter-body-html table.letter-meta { margin: 0 0 0.75em; }
          .letter-body-html table.letter-meta td { border: none; padding: 0; vertical-align: top; }
          .letter-body-html table.letter-meta td:last-child { text-align: right; }
          .letter-body-html hr { border: none; border-top: 1px solid #cbd5e1; margin: 0.75em 0; }
        `}</style>
      </>
    );
  }
  return (
    <div
      data-testid={testid}
      className={`whitespace-pre-wrap text-sm text-slate-800 font-serif leading-7 ${className}`}
      style={baseStyle || undefined}
    >
      {resolved}
    </div>
  );
}

export function letterPreviewHtml(body) {
  return isLetterHtml(body) ? body : plainToLetterHtml(body || "");
}
